/**
 * Pay-per-lead helpers — mirrors housing.zeus/src/controllers/owner/utils/payPerLead.js
 * and related owner util logic for client-side aggregation in SpaceX.
 */

export const PAY_PER_LEAD_DISPLAY_NAME = "PAY_PER_LEAD";
export const PRODUCT_NAME_PAY_PER_LEAD = "Pay Per Lead";

export const resalePackageCategory = "SELL";
export const rentPackageCategory = "RENT";

export const packageServiceMap = {
  SELL: "sell",
  RENT: "rent",
};

export const shouldShowPayPerLead = (opportunity = {}) => {
  const caseCategoryId =
    opportunity?.case_category_id ?? opportunity?.caseCategoryId;
  return Number(caseCategoryId) === 2;
};

const PACKAGE_MAP_PAY_PER_LEAD = {
  upsellPackage: "BASIC",
};

export const getEntityData = (entities = [], entityType = "") => {
  const entityObj =
    entities.find((entity) => entity.entityType === entityType) || {};
  return entityObj.entityIds || [];
};

/**
 * @param {object} params
 * @param {object} params.pricingData - v7 package-price payload (service key -> package[])
 * @param {string} params.packageName - e.g. PAY_PER_LEAD
 * @param {object} params.results - v4 package-price `results`
 */
export const getPayPerLeadPackageDetails = ({
  pricingData = {},
  packageName = PAY_PER_LEAD_DISPLAY_NAME,
  results = {},
}) => {
  for (const service of Object.keys(pricingData)) {
    const packages = pricingData[service] || [];
    const packageData = packages.find((pkg) => pkg.displayName === packageName);
    if (packageData) {
      const serviceType =
        service.toLowerCase() === packageServiceMap.RENT
          ? packageServiceMap.RENT
          : "buy";
      const listingIds = getEntityData(packageData.entities, "eligible_listing_ids");
      let upsellPackage = null;
      if (results && Object.keys(results).length) {
        const category =
          serviceType === "buy" ? resalePackageCategory : rentPackageCategory;
        const categoryResults = results[category] || {};
        const upsellPackageKey = PACKAGE_MAP_PAY_PER_LEAD.upsellPackage;
        upsellPackage = categoryResults[upsellPackageKey];
      }
      return {
        packageData,
        serviceType,
        service,
        listingIds,
        ...(upsellPackage ? { upsellPackage } : {}),
      };
    }
  }
  return {};
};

export const makeOwnerResLeadListQuery = ({
  id: flatId,
  service,
  page,
  page_size: size,
  isPaid,
  filter,
  source_name: sourceName,
  platform,
  leadTypeTag = "v1",
}) => ({
  service_type: service,
  page_number: page,
  page_size: size,
  flat_id: flatId,
  get_hits: true,
  is_paid: isPaid,
  filter,
  leadTypeTag,
  platform,
  ...(sourceName ? { source_name: sourceName } : {}),
});

export const getProductDataFromFsd = ({ property = {}, productName = "" } = {}) => {
  const { listing_slot_details: flatSlotDetails = [] } = property || {};
  for (const slot of flatSlotDetails) {
    const { extra_info: { products = [] } = {} } = slot || {};
    const desiredProducts =
      products.filter(({ product_name: name }) => name === productName) || [];
    if (desiredProducts.length) {
      return desiredProducts.reduce(
        (acc, { product_details: productDetails = {} } = {}) => {
          acc.initial_credits += Number(productDetails.initial_credits) || 0;
          acc.credits_consumed += Number(productDetails.credits_consumed) || 0;
          return { ...productDetails, ...acc };
        },
        {
          initial_credits: 0,
          credits_consumed: 0,
        }
      );
    }
  }
  return {};
};

const mapDynamicPropertiesKeyToValue = ({ keys: stringifiedKeys, value, obj }) => {
  let tempObj = obj;
  const keys = stringifiedKeys.split(".");
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      try {
        tempObj[key] = JSON.parse(value || "{}");
      } catch (e) {
        tempObj[key] = value;
      }
      return;
    }
    if (!tempObj[key]) {
      tempObj[key] = {};
    }
    tempObj = tempObj[key];
  });
};

export const formatDynamicPropertiesData = (data = []) => {
  const formattedData = {};
  data.forEach((item) => {
    const { propKey, propValue } = item || {};
    if (propKey) {
      mapDynamicPropertiesKeyToValue({
        keys: propKey,
        value: propValue,
        obj: formattedData,
      });
    }
  });
  return formattedData;
};

export const filterPricingParametersByLockedLeads = (
  pricingParameters = [],
  lockedLeadCount = 0
) => {
  return pricingParameters.filter(
    ({ pricingParameters: { initialCredits = 0 } = {} } = {}) =>
      initialCredits <= lockedLeadCount
  );
};

export const calculatePricePerPackage = (pricingParameters = []) => {
  const lastParam = pricingParameters[pricingParameters.length - 1];
  if (!lastParam) return 0;
  const discountedPrice = lastParam.discountedPrice || 0;
  const initialCredits = (lastParam.pricingParameters || {}).initialCredits || 0;
  return initialCredits > 0 ? Math.ceil(discountedPrice / initialCredits) : 0;
};

export const getMaxContinuousPricing = (pricingParams = []) => {
  if (!pricingParams || pricingParams.length === 0) {
    return 0;
  }
  const sorted = [...pricingParams]
    .map(({ pricingParameters: { initialCredits = 0 } = {} } = {}) =>
      parseInt(initialCredits, 10)
    )
    .filter((n) => !isNaN(n))
    .sort((a, b) => a - b);
  let maxContinuous = 0;
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i] === i + 1) {
      maxContinuous = sorted[i];
    } else {
      break;
    }
  }
  return maxContinuous;
};

export const buildPayPerLeadMetadata = ({
  pricingParameters = [],
  lockedLeadCount = 0,
  productDetails = {},
  upsellPackage = null,
  serviceType = "buy",
  taxDto = {},
  payPerLeadStaticData = {},
  showPayPerLead = false,
}) => {
  const {
    initial_credits: initialCredits = 0,
    credits_consumed: creditsConsumed = 0,
  } = productDetails;

  return {
    pricePerPackage: calculatePricePerPackage(pricingParameters),
    lockedEnquiries: lockedLeadCount,
    dashboardPriority: payPerLeadStaticData.dashboardPriority,
    unusedEnquiries: Math.max(0, initialCredits - creditsConsumed),
    purchasedEnquiries: initialCredits,
    thresholdForBiggerPackages: (() => {
      const maxC = getMaxContinuousPricing(pricingParameters);
      const thr = payPerLeadStaticData.thresholdForBiggerPackages;
      if (thr != null && !isNaN(Number(thr))) {
        return Math.min(maxC, Number(thr));
      }
      return maxC;
    })(),
    upsellPackage,
    showPayPerLead,
    serviceType:
      serviceType === packageServiceMap.RENT
        ? packageServiceMap.RENT
        : packageServiceMap.SELL,
    taxDto,
  };
};

export const isPayPerLeadPackageRow = (pkg) => {
  if (!pkg || typeof pkg !== "object") return false;
  if (pkg.displayName === PAY_PER_LEAD_DISPLAY_NAME) return true;
  const name = pkg.name || "";
  return typeof name === "string" && name.toUpperCase().includes("PAY_PER_LEAD");
};
